/**
 * Empty cart drawer "plinth" cards: choose a size, then add the piece without
 * leaving the drawer. Follows the same steps as assets/product-form.js
 * (CartLinesUpdateEvent, cart add with the cart-items sections, then a cart
 * refresh) so the drawer re-renders exactly as it does for a product page add.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

class MntPlinth extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.querySelectorAll('form[data-mnt-plinth-form]').forEach((form) => {
      form.addEventListener('change', () => this.#syncLabel(form));
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#add(form);
      });
    });

    this.#buildDots();
  }

  /** @param {HTMLFormElement} form */
  #syncLabel(form) {
    const chosen = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]:checked'));
    const label = form.querySelector('[data-mnt-plinth-label]');
    const error = form.querySelector('[data-mnt-plinth-error]');
    if (error) error.hidden = true;
    if (label) label.textContent = chosen ? `Place on the plinth · Add ${chosen.dataset.label}` : 'Choose a size';
  }

  /** @param {HTMLFormElement} form */
  async #add(form) {
    const chosen = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]:checked'));
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('[data-mnt-plinth-add]'));
    const error = /** @type {HTMLElement | null} */ (form.querySelector('[data-mnt-plinth-error]'));

    if (!chosen) {
      if (error) {
        error.textContent = 'Choose a size';
        error.hidden = false;
      }
      form.querySelector('input[name="id"]:not([disabled])')?.focus();
      return;
    }
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    formData.set('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: chosen.value, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const config = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      const ajaxCart = await this.#refreshCart();
      const didError = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'mnt-empty-cart-plinth',
          sourceId: form.dataset.productId,
          itemCount: 1,
          productId: form.dataset.productId,
          sections: didError ? undefined : response.sections,
          didError,
        },
      });

      if (didError && error) {
        error.textContent = response.message || response.description || 'This piece could not be added.';
        error.hidden = false;
      }
    } catch (err) {
      deferred.reject(err);
      if (error) {
        error.textContent = 'This piece could not be added. Please try again.';
        error.hidden = false;
      }
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  async #refreshCart() {
    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
  }

  #buildDots() {
    const track = this.querySelector('.mnt-plinth__track');
    const dots = this.querySelector('[data-mnt-plinth-dots]');
    const cards = track ? Array.from(track.children) : [];
    if (!track || !dots || cards.length < 2) return;

    dots.replaceChildren(...cards.map(() => document.createElement('i')));
    const mark = () => {
      const step = cards[0].getBoundingClientRect().width + 12;
      const index = Math.min(cards.length - 1, Math.round(track.scrollLeft / step));
      Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-on', i === index));
    };
    const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-mnt-plinth-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-mnt-plinth-next]'));
    const step = () => cards[0].getBoundingClientRect().width + 12;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const go = (/** @type {number} */ dir) => track.scrollBy({ left: dir * step(), behavior: reduce ? 'auto' : 'smooth' });
    prev?.addEventListener('click', () => go(-1));
    next?.addEventListener('click', () => go(1));

    const sync = () => {
      mark();
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
    };
    track.addEventListener('scroll', sync, { passive: true });
    // The drawer is hidden (zero width) when this runs, so re-check whenever the
    // row gets its real size, e.g. when the drawer opens or the window resizes.
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
    sync();
  }
}

if (!customElements.get('mnt-plinth')) customElements.define('mnt-plinth', MntPlinth);
